import type { JSX } from 'react';
import type { SmsSettingsBody, UpdateUseCaseBody } from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { SmsSimulator } from '../SmsSimulator';
import { CallingCodesField, SenderField } from '../SmsSettingsInputs';
import { useToast } from '../toast';
import { internalFetch, internalSend, validationDetail } from '../../lib/api';
import {
  applicableRule,
  DEFAULT_CALLING_CODES,
  describeRule,
  displayCallingCodes,
  formatCallingCodes,
  type PricingRule,
  pricePerSms,
  readSmsSettings,
  ruleInEffect,
  smsSettingsOf,
  type UseCase,
} from '../../lib/use-cases';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { FieldError } from '@/components/ui/field';
import { KvList, KvRow } from '@/components/ui/kv';

const PENDING_NOTE =
  'Une modification s’applique aussi aux SMS en attente d’envoi. Un SMS vers un indicatif retiré est refusé à l’envoi, sans facturation.';

/**
 * SMS settings of an SMS use case (lot 17): sender and allowed calling codes,
 * editable (PATCH), with the simulator at the price the owner client is billed
 * (its override, else the default rule). Both settings are read again when a
 * message leaves, so a change also applies to the SMS still waiting: an SMS to
 * a calling code removed meanwhile fails unbilled, without being sent (D-SMS.4).
 */
export function SmsSettingsPanel({ useCase }: { useCase: UseCase }): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const sms = smsSettingsOf(useCase);

  const [open, setOpen] = useState(false);
  const [sender, setSender] = useState('');
  const [callingCodes, setCallingCodes] = useState('');
  const [formError, setFormError] = useState('');

  // Same query as the pricing panel: one request, shared cache.
  const rules = useQuery({
    queryKey: ['pricing-rules', useCase.id],
    queryFn: () =>
      internalFetch<{ rules: PricingRule[] }>(`/internal/use-cases/${useCase.id}/pricing-rules`),
  });
  const allRules = rules.data?.rules ?? [];
  const now = Date.now();
  const defaultRule = ruleInEffect(allRules, null, now);
  // The simulator estimates what the owner client is actually billed (PRD 8.3).
  const applied = applicableRule(allRules, useCase.ownerClientId, now);

  const openEdit = (): void => {
    setSender(sms?.sender ?? '');
    setCallingCodes(
      sms === null ? DEFAULT_CALLING_CODES : formatCallingCodes(sms.allowedCallingCodes),
    );
    setFormError('');
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: (settings: SmsSettingsBody) => {
      const body: UpdateUseCaseBody = { sms: settings };
      return internalSend<UseCase>('PATCH', `/internal/use-cases/${useCase.id}`, body);
    },
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['use-cases'] });
      notify('Réglages SMS enregistrés');
    },
    onError: (error) => {
      setFormError(validationDetail(error) ?? 'Échec de l’enregistrement des réglages SMS.');
    },
  });

  const submit = (): void => {
    setFormError('');
    const settings = readSmsSettings(sender, callingCodes);
    if (!settings.ok) {
      setFormError(settings.error);
      return;
    }
    save.mutate(settings.value);
  };

  return (
    <>
      <KvList>
        <KvRow label="Expéditeur" mono>
          <span className="font-medium">{sms?.sender ?? '—'}</span>
        </KvRow>
        <KvRow label="Indicatifs autorisés" mono>
          {sms === null ? '—' : displayCallingCodes(sms.allowedCallingCodes)}
        </KvRow>
        <KvRow label="Prix par défaut en vigueur" mono>
          {defaultRule === undefined ? '—' : `${describeRule(defaultRule.rule)} HT`}
        </KvRow>
      </KvList>
      <Callout variant="warning">{PENDING_NOTE}</Callout>
      <SmsSimulator
        pricePerSmsMicroEur={applied === null ? null : pricePerSms(applied.rule.rule)}
        priceSource={applied?.source}
      />
      <div>
        <Button type="button" variant="outline" onClick={openEdit}>
          Modifier les réglages SMS
        </Button>
      </div>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title="Modifier les réglages SMS"
            description={
              <>
                Cas d’usage <span className="font-mono">{useCase.key}</span>. {PENDING_NOTE}
              </>
            }
          />
          <DialogBody>
            <SenderField value={sender} onChange={setSender} />
            <CallingCodesField value={callingCodes} onChange={setCallingCodes} />
            {formError.length > 0 ? <FieldError>{formError}</FieldError> : null}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Annuler
            </Button>
            <Button type="button" size="lg" onClick={submit} disabled={save.isPending}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
